import React, { useEffect, useState } from 'react'
import { Tabs, Tab as MuiTab, TabsProps } from '@mui/material'

import Icon from 'particles/Icon'
import Text, { TextScale } from 'particles/Text'
import Tooltip from 'particles/Tooltip'
import styled from 'particles/themes/styled'
import { Theme } from 'particles/themes'

type TabType = 'main' | 'steps' | 'secondary'
type TabSize = 'm' | 'l'
type TabItemAlign = 'left' | 'center'

interface SizeMapProps {
    minItemWidth: string
    textScale: TextScale
    selectedTextScale: TextScale
}

const sizeMap: Record<TabSize, SizeMapProps> = {
    m: {
        minItemWidth: '60px',
        textScale: 'bodyS500',
        selectedTextScale: 'bodyS700',
    },
    l: {
        minItemWidth: '80px',
        textScale: 'bodyM400',
        selectedTextScale: 'bodyM700',
    },
}

const formSize: Partial<Record<TabType, TabSize>> = {
    main: 'l',
    steps: 'm',
}

export interface TabOption {
    id: string
    label: string
    isError?: boolean
    disabled?: boolean
    onClick?: () => void
    isHidden?: boolean
}

interface TabProps {
    tabs: TabOption[]
    width?: string
    form?: TabType
    selectedTab?: string
    tabItemAlign?: TabItemAlign
    dataTestId?: string
    isDisabledClickable?: boolean
    className?: string
}

/**
 * tab item align left -> overflow: scroll
 * tab item align center -> width fit & overflow tab ellipsis on 2nd line
 */
const Tab = ({
    form = 'main',
    tabs,
    width = '100%',
    selectedTab,
    tabItemAlign = 'center',
    dataTestId,
    isDisabledClickable = false,
    className,
}: TabProps) => {
    const [selectedTabId, setSelectedTabId] = useState<string | null>(null)
    const [tooltipLabel, setTooltipLabel] = useState<Record<string, true>>({})
    const size = formSize[form] || 'm'
    const { minItemWidth, selectedTextScale, textScale } = sizeMap[size]

    const visibleTabs = tabs.filter(tab => !tab.isHidden)

    useEffect(() => {
        if (selectedTab && selectedTab !== selectedTabId) {
            setSelectedTabId(selectedTab)
        } else if (selectedTabId === null && tabs.length) {
            setSelectedTabId(visibleTabs[0].id)
        }
    }, [selectedTabId, tabs, selectedTabId])

    // reset tooltip when form or alignment is change
    useEffect(() => {
        setTooltipLabel({})
    }, [form, tabItemAlign])

    const showStepSeparator = (idx: number) => {
        const isNotLastIndex = idx < visibleTabs.length - 1
        return isNotLastIndex && form === 'steps'
    }

    // to display tooltip only when label overflow
    const onTabItemHover = (scrollHeight: number, id: string) => {
        if (tooltipLabel[id]) return

        const MAX_TEXT_HEIGHT = 50
        if (scrollHeight > MAX_TEXT_HEIGHT) {
            const label: Record<string, true> = {
                ...tooltipLabel,
                [id]: true,
            }
            setTooltipLabel(label)
        }
    }

    const handleTabClick = (id: string) => {
        const tab = visibleTabs.find(t => t.id === id)

        if (tab && isDisabledClickable) {
            tab.onClick?.()
        }
        if (!tab || tab.disabled) {
            return
        }

        setSelectedTabId(tab.id)
        tab.onClick?.()
    }

    const getTextScale = (isSelected: boolean, type: TabType) => {
        if (type === 'secondary') {
            return isSelected ? selectedTextScale : textScale
        }

        return selectedTextScale
    }

    if (!visibleTabs.length || !selectedTabId) return null

    return (
        <TabContainer
            align={tabItemAlign}
            width={width}
            variant={tabItemAlign === 'left' ? 'scrollable' : 'fullWidth'}
            scrollButtons={false}
            value={selectedTabId}
            form={form}
            onChange={(_e, value) => {
                handleTabClick(value)
            }}
            className={className}
        >
            {visibleTabs.map((tab, idx) => {
                const isSelected = tab.id === selectedTabId
                const separator = showStepSeparator(idx) ? (
                    <TabStepContainer key={`separator-${tab.id}`}>
                        <Icon name="caretRight" size="16px" color="tertiary" className="caret-icon" />
                    </TabStepContainer>
                ) : null

                return [
                    <TabItem
                        key={`tab-${tab.id}`}
                        data-testid={dataTestId}
                        sx={{
                            '&.MuiButtonBase-root': {
                                minWidth: form === 'secondary' ? 42 : minItemWidth,
                                padding: form === 'secondary' ? '4px 8px !important' : '12px 16px !important',
                            },
                        }}
                        align={tabItemAlign}
                        value={tab.id}
                        label={
                            // when label is undefined, tooltip will not display on purpose
                            <Tooltip label={tooltipLabel[tab.id] ? tab.label : undefined} placement="bottom">
                                <TabItemTextContainer>
                                    <Text
                                        scale={getTextScale(isSelected, form)}
                                        color={tab.isError ? 'error' : isSelected ? 'action' : 'neutralAction'}
                                        className="tab-label"
                                        onMouseEnter={e => onTabItemHover(e.currentTarget.scrollHeight, tab.id)}
                                    >
                                        {tab.label}
                                    </Text>

                                    {tab.isError && <ErrorDots form={form} />}
                                </TabItemTextContainer>
                            </Tooltip>
                        }
                        form={form}
                        isSelected={isSelected}
                    />,
                    separator,
                ]
            })}
        </TabContainer>
    )
}

export default Tab

// helper for tab gap, to be more readable
const getTabGap = (theme: Theme, align: TabItemAlign, form: TabType) => {
    if ((form = 'secondary')) {
        return theme.spacing[300]
    } else if (align === 'left') {
        return theme.spacing[500]
    } else if (form === 'steps') {
        return theme.spacing[300]
    }
    return '0px'
}
interface TabContainerProps extends TabsProps {
    width: string
    align: TabItemAlign
    form: TabType
}
const TabContainer = styled(Tabs)<TabContainerProps>`
    all: unset;
    width: ${({ width }) => width};
    max-height: 64px;
    display: flex;
    box-sizing: border-box;
    overflow-y: hidden;
    overflow-x: ${({ align, form }) => (form === 'secondary' || align === 'left' ? 'auto' : 'hidden')};

    &.MuiTabs-root {
        ${({ form }) =>
            form === 'secondary' && {
                minHeight: 28,
            }};
    }

    .MuiTabs-flexContainer {
        box-shadow: inset 0 -2px 0 ${({ theme }) => theme.colors.border.primary};
        gap: ${({ theme, align, form }) => getTabGap(theme, align, form)};
        max-height: 64px;
        max-width: unset;
        width: ${({ width, align, form }) => (form === 'secondary' || align === 'left' ? 'fit-content' : width)};
        min-width: ${({ width }) => width};
        ${({ form }) =>
            form === 'secondary' && {
                boxShadow: 'none',
            }};
    }

    .MuiTabs-indicator {
        display: ${({ form }) => (form === 'secondary' ? 'none' : 'inline-block')};
        background: ${({ theme }) => theme.colors.text.action};
    }

    .caret-icon {
        flex: 0 0 fit-content;
    }
`

interface TabItemProps {
    align: TabItemAlign
    form: TabType
    isSelected: boolean
}

const TabItem = styled(MuiTab)<TabItemProps>`
    &.MuiButtonBase-root {
        font-family: inherit;
        text-tranform: none;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: ${({ align, form }) => (form === 'secondary' || align === 'left' ? '0 0 fit-content' : '1')};
        padding: ${({ theme }) => `${theme.spacing[300]} 0px`};
        width: fit-content;
        text-wrap: wrap;
        overflow: visible;
        max-width: unset;

        ${({ theme, form, isSelected }) =>
            form === 'secondary' && {
                minWidth: 42,
                minHeight: 28,
                borderRadius: 4,
                background: isSelected ? theme.colors.illustration.tertiary : undefined,
                padding: '0 3px',
            }};

        &:hover {
            ${({ theme, form }) =>
                form === 'secondary' && {
                    background: theme.colors.surface.neutralActionPastelHover,
                }};
        }

        .MuiTouchRipple-child {
            background: transparent;
        }
    }

    .tab-label {
        box-sizing: border-box;
        text-align: center;
        flex: ${({ align, form }) => (form === 'secondary' || align === 'left' ? '0 0 fit-content' : 'unset')};
        text-wrap: ${({ align, form }) => (form === 'secondary' || align === 'left' ? 'nowrap' : 'unset')};
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        cursor: pointer;

        &:hover {
            ${({ theme, form }) =>
                form === 'secondary' && {
                    color: theme.colors.text.primary,
                }};
        }
    }
`

// error dot placement helper
const TabItemTextContainer = styled.div`
    text-transform: none;
    font-family: inherit;
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
`

const TabStepContainer = styled.div`
    display: flex;
    align-items: center;
`

interface ErrorDotsProps {
    form: TabType
}

const ErrorDots = styled.div<ErrorDotsProps>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.surface.error};
`
